/* eslint-disable no-comments/disallowComments */
import React from 'react';
import { styled } from '@mui/material';
import { Link } from 'react-router-dom';
import { Colors } from 'library';
import { RBaseButton } from './RBaseButton';

type RCardPadding = 'sm' | 'md' | 'lg';
type RCardTone = 'default' | 'muted' | 'error';

interface RCardBaseProps {
    children: React.ReactNode;
    /** Inner padding: `sm` (16px), `md` (24px, default), or `lg` (32px). */
    padding?: RCardPadding;
    /** Background tone: `default` (white), `muted` (faintGrey), or `success` (lightTurq + mediumTurq border). */
    tone?: RCardTone;
    /** If true, adds hover state (shadow + border tint). Automatically true when `to` or `onClick` is provided. */
    interactive?: boolean;
    /** If true, stretches to 100% of parent height (useful in grid/kanban layouts). */
    fullHeight?: boolean;
    /** If true, applies `flex: 1` so the card grows to fill available space next to flex siblings. */
    grow?: boolean;
    className?: string;
}

interface RCardLinkProps extends RCardBaseProps {
    /** If provided, renders the card as a react-router Link. */
    to: string;
    onClick?: never;
    disabled?: never;
}

interface RCardButtonProps extends RCardBaseProps {
    to?: undefined;
    /** If provided (or `interactive` is true), the card renders as a `<button>` element. */
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    /** Only applies when the card renders as a button. */
    disabled?: boolean;
}

interface RCardStaticProps extends RCardBaseProps {
    to?: undefined;
    onClick?: undefined;
    interactive?: false;
    disabled?: never;
}

type RCardProps = RCardLinkProps | RCardButtonProps | RCardStaticProps;

const PADDING_PX: Record<RCardPadding, number> = { sm: 16, md: 24, lg: 32 };
const TONE_BG: Record<RCardTone, string> = { default: Colors.white, muted: Colors.faintGrey, error: Colors.lightRed };
const TONE_BORDER: Record<RCardTone, string> = {
    default: Colors.lightGrey,
    muted: Colors.lightGrey,
    error: Colors.mediumRed,
};

type CardStyleProps = {
    $padding: RCardPadding;
    $tone: RCardTone;
    $interactive: boolean;
    $fullHeight: boolean;
    $grow: boolean;
};

const cardStyles = ({ $padding, $tone, $interactive, $fullHeight, $grow }: CardStyleProps) => ({
    display: 'block',
    backgroundColor: TONE_BG[$tone],
    border: `1px solid ${TONE_BORDER[$tone]}`,
    borderRadius: 12,
    padding: PADDING_PX[$padding],
    boxSizing: 'border-box' as const,
    height: $fullHeight ? '100%' : undefined,
    flex: $grow ? 1 : undefined,
    color: 'inherit',
    textDecoration: 'none',
    transition: $interactive ? 'box-shadow 0.2s ease, border-color 0.2s ease' : undefined,
    cursor: $interactive ? ('pointer' as const) : undefined,
    '&:hover': $interactive
        ? {
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
              borderColor: Colors.mediumTurq,
              textDecoration: 'none',
          }
        : undefined,
});

const shouldForwardCardProp = (prop: string) =>
    !['$padding', '$tone', '$interactive', '$fullHeight', '$grow'].includes(prop);

const DivCard = styled('div', { shouldForwardProp: (p) => shouldForwardCardProp(p as string) })<CardStyleProps>(
    cardStyles,
);
const ButtonCard = styled(RBaseButton, {
    shouldForwardProp: (p) => shouldForwardCardProp(p as string),
})<CardStyleProps>((props) => ({
    ...cardStyles(props),
    textAlign: 'left' as const,
    width: '100%',
}));
const LinkCard = styled(Link, { shouldForwardProp: (p) => shouldForwardCardProp(p as string) })<CardStyleProps>(
    cardStyles,
);

export const RCard = React.forwardRef<HTMLElement, RCardProps>(function RCard(props, ref) {
    const {
        children,
        padding = 'md',
        tone = 'default',
        interactive,
        fullHeight = false,
        grow = false,
        className,
    } = props;

    if (props.to !== undefined) {
        return (
            <LinkCard
                ref={ref as React.Ref<HTMLAnchorElement>}
                to={props.to}
                $padding={padding}
                $tone={tone}
                $interactive
                $fullHeight={fullHeight}
                $grow={grow}
                className={className}
            >
                {children}
            </LinkCard>
        );
    }

    const isButton = Boolean(props.onClick || interactive);
    if (isButton) {
        return (
            <ButtonCard
                ref={ref as React.Ref<HTMLButtonElement>}
                type="button"
                onClick={props.onClick}
                disabled={props.disabled}
                $padding={padding}
                $tone={tone}
                $interactive
                $fullHeight={fullHeight}
                $grow={grow}
                className={className}
            >
                {children}
            </ButtonCard>
        );
    }

    return (
        <DivCard
            ref={ref as React.Ref<HTMLDivElement>}
            $padding={padding}
            $tone={tone}
            $interactive={false}
            $fullHeight={fullHeight}
            $grow={grow}
            className={className}
        >
            {children}
        </DivCard>
    );
});
/* eslint-enable no-comments/disallowComments */
